import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Lock, Ban } from "lucide-react";
import LikelinessSelect from "./LikelinessSelect";

// Format number as currency display
const formatCurrency = (value) => {
  if (!value && value !== 0) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return "";
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const RenewalItem = ({ renewal, index, isLast, onRenewalChange, onUnpaidRequest }) => {
  const isPaid = renewal.paid;
  const isCancelledOrFree = ["CANCELLED", "FREE"].includes(renewal.likeliness);
  const isDisabled = isPaid || isCancelledOrFree;
  
  const paidLabel = isPaid ? "Paid ✓" : (isLast ? "Mark as Paid (creates next renewal)" : "Paid");

  const handlePaidChange = (checked) => {
    if (isPaid && !checked) {
      onUnpaidRequest(index);
    } else {
      onRenewalChange(index, 'paid', checked);
    }
  };

  const handlePriceChange = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    onRenewalChange(index, 'renewal_price', val);
  };

  // Show formatted when disabled/paid, raw when editable
  const displayPrice = isDisabled ? formatCurrency(renewal.renewal_price) : renewal.renewal_price;

  // Determine styling based on state
  const getContainerStyle = () => {
    if (isPaid) return 'bg-emerald-50 border-emerald-200';
    if (isCancelledOrFree) return 'bg-slate-100 border-slate-300 opacity-60';
    return 'bg-slate-50';
  };

  const getInputStyle = () => {
    if (isPaid) return 'bg-emerald-100/50 text-emerald-800 border-emerald-300';
    if (isCancelledOrFree) return 'bg-slate-200/50 text-slate-500 border-slate-300';
    return '';
  };

  return (
    <div 
      className={`p-4 rounded-lg border ${getContainerStyle()}`} 
      data-testid={`renewal-${index}`}
    >
      <div className="flex justify-between mb-3">
        <span className="text-sm font-semibold text-slate-700">
          Renewal {index + 1} 
          {isPaid && <span className="text-emerald-600 text-xs ml-2">✓ Paid</span>}
          {isCancelledOrFree && !isPaid && (
            <span className="text-slate-500 text-xs ml-2">
              {renewal.likeliness === "CANCELLED" ? "• Cancelled" : "• Free"}
            </span>
          )}
        </span>
        {isPaid && (
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <Lock className="w-3 h-3" /> Locked
          </span>
        )}
        {isCancelledOrFree && !isPaid && (
          <span className="flex items-center gap-1 text-xs text-slate-400">
            <Ban className="w-3 h-3" /> No revenue expected
          </span>
        )}
      </div>
      <div className="grid grid-cols-4 gap-3 mb-3">
        <div>
          <Label className={`text-xs ${isDisabled ? 'text-slate-400' : ''}`}>Date</Label>
          <Input 
            type="date" 
            value={renewal.renewal_date} 
            onChange={e => onRenewalChange(index, 'renewal_date', e.target.value)} 
            className={`h-9 ${isDisabled ? getInputStyle() + ' cursor-not-allowed' : ''}`}
            disabled={isDisabled}
            data-testid={`renewal-${index}-date`}
          />
        </div>
        <div>
          <Label className={`text-xs ${isDisabled ? 'text-slate-400' : ''}`}>Price</Label>
          <div className="relative">
            <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-sm ${isDisabled ? 'text-slate-400' : 'text-slate-500'}`}>$</span>
            <Input 
              type="text"
              inputMode="decimal"
              value={displayPrice}
              onChange={handlePriceChange}
              className={`h-9 pl-6 ${isDisabled ? getInputStyle() + ' cursor-not-allowed' : ''}`}
              placeholder="0.00"
              disabled={isDisabled}
              data-testid={`renewal-${index}-price`}
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Likeliness</Label>
          <LikelinessSelect 
            value={renewal.likeliness} 
            onValueChange={v => onRenewalChange(index, 'likeliness', v)}
            disabled={isPaid}
          />
        </div>
        <div className="flex items-end pb-1">
          <div className="flex items-center space-x-2">
            <Checkbox 
              checked={isPaid} 
              onCheckedChange={handlePaidChange}
              disabled={isCancelledOrFree}
              data-testid={`renewal-${index}-paid`}
            />
            <Label className={`text-xs ${isCancelledOrFree ? 'text-slate-400' : ''}`}>
              {isCancelledOrFree ? 'N/A' : paidLabel}
            </Label>
          </div>
        </div>
      </div>
      <div>
        <Label className={`text-xs ${isDisabled ? 'text-slate-400' : ''}`}>Notes</Label>
        <Textarea 
          value={renewal.notes || ""} 
          onChange={e => onRenewalChange(index, 'notes', e.target.value)} 
          placeholder="Notes for this renewal period..." 
          rows={2} 
          className={`text-sm ${isDisabled ? getInputStyle() + ' cursor-not-allowed' : ''}`}
          disabled={isDisabled}
          data-testid={`renewal-${index}-notes`}
        />
      </div>
    </div>
  );
};

export default RenewalItem;
